import { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AudioWaveform,
  Mic,
  Square,
  Play,
  Pause,
  Trash2,
  Save,
  Clock,
  Music2,
  Loader2,
  Volume2,
  ChevronLeft,
  Download,
  ListMusic
} from "lucide-react";
import { toast } from "sonner";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RecordPage = () => {
  const { getAuthHeaders } = useAuth();
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  
  // Playback
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackTime, setPlaybackTime] = useState(0);
  
  // Metronome
  const [metronomeEnabled, setMetronomeEnabled] = useState(false);
  const [metronomeBpm, setMetronomeBpm] = useState(120);
  const [metronomeVolume, setMetronomeVolume] = useState(50);
  
  // Saved recordings
  const [recordings, setRecordings] = useState([]);
  const [loadingRecordings, setLoadingRecordings] = useState(true);
  const [stats, setStats] = useState(null);
  const [savedPlaybackUrl, setSavedPlaybackUrl] = useState(null);
  const [savedPlayingId, setSavedPlayingId] = useState(null);
  const [savedLoadingId, setSavedLoadingId] = useState(null);
  
  // Save dialog
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveTitle, setSaveTitle] = useState("");
  const [saveNotes, setSaveNotes] = useState("");
  const [saving, setSaving] = useState(false);
  
  // Refs
  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioRef = useRef(null);
  const savedAudioRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const metronomeIntervalRef = useRef(null);

  // Fetch recordings
  const fetchRecordings = useCallback(async () => {
    try {
      const [recordingsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/recordings`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/recordings/stats/weekly`, { headers: getAuthHeaders() })
      ]);
      setRecordings(recordingsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching recordings:", error);
    } finally {
      setLoadingRecordings(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchRecordings();
    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (metronomeIntervalRef.current) clearInterval(metronomeIntervalRef.current);
      if (audioContextRef.current) audioContextRef.current.close();
    };
  }, [fetchRecordings]);

  useEffect(() => {
    return () => {
      if (savedPlaybackUrl) URL.revokeObjectURL(savedPlaybackUrl);
    };
  }, [savedPlaybackUrl]);

  // Metronome
  const playMetronomeClick = useCallback(() => {
    if (!audioContextRef.current) return;
    
    const ctx = audioContextRef.current;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.connect(gain);
    gain.connect(ctx.destination);
    
    osc.frequency.value = 1000;
    gain.gain.value = metronomeVolume / 100 * 0.3;
    
    osc.start(ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.05);
    osc.stop(ctx.currentTime + 0.05);
  }, [metronomeVolume]);

  useEffect(() => {
    if (metronomeEnabled && isRecording && !isPaused) {
      const interval = 60000 / metronomeBpm;
      metronomeIntervalRef.current = setInterval(playMetronomeClick, interval);
    } else {
      if (metronomeIntervalRef.current) {
        clearInterval(metronomeIntervalRef.current);
      }
    }
    
    return () => {
      if (metronomeIntervalRef.current) clearInterval(metronomeIntervalRef.current);
    };
  }, [metronomeEnabled, isRecording, isPaused, metronomeBpm, playMetronomeClick]);

  // Start recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      mediaRecorderRef.current = new MediaRecorder(stream);
      chunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };
      
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start(100);
      setIsRecording(true);
      setRecordingTime(0);
      
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
      toast.success("Grabando...");
    } catch (error) {
      toast.error("No se pudo acceder al micrófono");
      console.error(error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      if (timerRef.current) clearInterval(timerRef.current);
      toast.success("Grabación completada");
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      if (isPaused) {
        mediaRecorderRef.current.resume();
        timerRef.current = setInterval(() => {
          setRecordingTime(prev => prev + 1);
        }, 1000);
      } else {
        mediaRecorderRef.current.pause();
        if (timerRef.current) clearInterval(timerRef.current);
      }
      setIsPaused(!isPaused);
    }
  };

  const discardRecording = () => {
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingTime(0);
    setPlaybackTime(0);
  };

  // Save recording
  const handleSave = async () => {
    if (!audioBlob || !saveTitle.trim()) {
      toast.error("Ingresa un título");
      return;
    }
    
    setSaving(true);
    try {
      // Create recording metadata
      const createRes = await axios.post(`${API_URL}/recordings`, {
        title: saveTitle,
        duration_seconds: recordingTime,
        notes: saveNotes || null,
        bpm_used: metronomeEnabled ? metronomeBpm : null
      }, { headers: getAuthHeaders() });
      
      // Upload audio
      const formData = new FormData();
      formData.append("file", audioBlob, "recording.webm");
      
      await axios.post(
        `${API_URL}/recordings/${createRes.data.id}/upload`,
        formData,
        { headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" } }
      );
      
      toast.success("Grabación guardada");
      setShowSaveDialog(false);
      setSaveTitle("");
      setSaveNotes("");
      discardRecording();
      fetchRecordings();
    } catch (error) {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const deleteRecording = async (id) => {
    if (!window.confirm("¿Eliminar esta grabación?")) return;
    
    try {
      await axios.delete(`${API_URL}/recordings/${id}`, { headers: getAuthHeaders() });
      toast.success("Grabación eliminada");
      fetchRecordings();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const toggleSavedRecordingPlayback = async (rec) => {
    if (!rec.audio_url) {
      toast.info("Esta grabacion no tiene audio guardado");
      return;
    }

    if (savedPlayingId === rec.id && savedAudioRef.current) {
      savedAudioRef.current.pause();
      setSavedPlayingId(null);
      return;
    }

    setSavedLoadingId(rec.id);
    try {
      if (savedAudioRef.current) {
        savedAudioRef.current.pause();
      }
      if (savedPlaybackUrl) {
        URL.revokeObjectURL(savedPlaybackUrl);
      }

      const response = await axios.get(`${API_URL}/recordings/${rec.id}/audio`, {
        headers: getAuthHeaders(),
        responseType: "blob",
      });
      const objectUrl = URL.createObjectURL(response.data);
      setSavedPlaybackUrl(objectUrl);
      setSavedPlayingId(rec.id);

      window.setTimeout(() => {
        savedAudioRef.current?.play().catch(() => {
          toast.error("No se pudo reproducir la grabacion");
          setSavedPlayingId(null);
        });
      }, 0);
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo cargar el audio guardado");
      setSavedPlayingId(null);
    } finally {
      setSavedLoadingId(null);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Playback controls
  const togglePlayback = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  return (
    <div className="min-h-screen bg-[#09090b]">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-panel">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="flex items-center gap-2" data-testid="logo">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                <AudioWaveform className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl text-white font-['Manrope']">SteelMora</span>
            </Link>
            <span className="text-gray-600">/</span>
            <span className="text-white font-medium">Grabadora</span>
          </div>
          
          <Link to="/setlists">
            <Button variant="outline" className="border-white/10 text-gray-300 gap-2">
              <ListMusic className="w-4 h-4" />
              Setlists
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
            <Card className="bg-[#18181b] border-white/10">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Mic className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.total_recordings}</p>
                  <p className="text-xs text-gray-500">Esta semana</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#18181b] border-white/10">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-secondary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.total_minutes}m</p>
                  <p className="text-xs text-gray-500">Tiempo de práctica</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#18181b] border-white/10 col-span-2 md:col-span-1">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                  <Music2 className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{recordings.length}</p>
                  <p className="text-xs text-gray-500">Total grabaciones</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Recording Interface */}
        <Card className="bg-[#18181b] border-white/10 mb-8">
          <CardHeader>
            <CardTitle className="text-white font-['Manrope'] flex items-center gap-2">
              <Mic className="w-5 h-5 text-primary" />
              Grabar Práctica
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Timer Display */}
            <div className="text-center py-8">
              <div className={`text-7xl font-bold font-['JetBrains_Mono'] ${isRecording ? "text-red-500" : "text-white"}`}>
                {formatTime(recordingTime)}
              </div>
              {isRecording && (
                <div className="flex items-center justify-center gap-2 mt-2">
                  <div className={`w-3 h-3 rounded-full ${isPaused ? "bg-yellow-500" : "bg-red-500 animate-pulse"}`} />
                  <span className="text-gray-400">{isPaused ? "Pausado" : "Grabando"}</span>
                </div>
              )}
            </div>

            {/* Recording Controls */}
            <div className="flex items-center justify-center gap-4">
              {!isRecording && !audioBlob && (
                <Button
                  size="lg"
                  onClick={startRecording}
                  className="h-16 w-16 rounded-full bg-red-500 hover:bg-red-600"
                  data-testid="start-record-btn"
                >
                  <Mic className="w-8 h-8" />
                </Button>
              )}
              
              {isRecording && (
                <>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={pauseRecording}
                    className={`h-12 w-12 ${isPaused ? "border-yellow-500 text-yellow-500" : "border-white/20 text-white"}`}
                  >
                    {isPaused ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
                  </Button>
                  <Button
                    size="lg"
                    onClick={stopRecording}
                    className="h-16 w-16 rounded-full bg-white hover:bg-gray-200"
                    data-testid="stop-record-btn"
                  >
                    <Square className="w-8 h-8 text-black" />
                  </Button>
                </>
              )}
              
              {audioBlob && !isRecording && (
                <>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={discardRecording}
                    className="h-12 w-12 border-red-500/50 text-red-500"
                  >
                    <Trash2 className="w-5 h-5" />
                  </Button>
                  <Button
                    size="lg"
                    onClick={togglePlayback}
                    className="h-16 w-16 rounded-full bg-primary hover:bg-primary/90"
                  >
                    {isPlaying ? <Pause className="w-8 h-8" /> : <Play className="w-8 h-8 ml-1" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setShowSaveDialog(true)}
                    className="h-12 w-12 border-secondary/50 text-secondary"
                    data-testid="save-record-btn"
                  >
                    <Save className="w-5 h-5" />
                  </Button>
                </>
              )}
            </div>

            {/* Audio Player */}
            {audioUrl && (
              <audio
                ref={audioRef}
                src={audioUrl}
                onEnded={() => setIsPlaying(false)}
                onTimeUpdate={(e) => setPlaybackTime(Math.floor(e.target.currentTime))}
                className="hidden"
              />
            )}

            {/* Metronome */}
            <div className="pt-4 border-t border-white/10">
              <div className="flex items-center justify-between mb-4">
                <span className="text-gray-300 font-medium">Metrónomo</span>
                <Button
                  variant={metronomeEnabled ? "default" : "outline"}
                  size="sm"
                  onClick={() => setMetronomeEnabled(!metronomeEnabled)}
                  className={metronomeEnabled ? "bg-secondary" : "border-white/10 text-gray-300"}
                >
                  {metronomeEnabled ? "ON" : "OFF"}
                </Button>
              </div>
              
              {metronomeEnabled && (
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-400">BPM</span>
                      <span className="text-white font-mono">{metronomeBpm}</span>
                    </div>
                    <Slider
                      value={[metronomeBpm]}
                      onValueChange={(v) => setMetronomeBpm(v[0])}
                      min={40}
                      max={240}
                      step={1}
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-400">Volumen</span>
                      <span className="text-white font-mono">{metronomeVolume}%</span>
                    </div>
                    <Slider
                      value={[metronomeVolume]}
                      onValueChange={(v) => setMetronomeVolume(v[0])}
                      min={0}
                      max={100}
                      step={5}
                    />
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Saved Recordings */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white font-['Manrope']">Mis Grabaciones</h2>
          
          {loadingRecordings ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 text-primary animate-spin" />
            </div>
          ) : recordings.length === 0 ? (
            <Card className="bg-[#18181b] border-white/10 border-dashed">
              <CardContent className="py-12 text-center">
                <Mic className="w-12 h-12 text-gray-700 mx-auto mb-3" />
                <p className="text-gray-400">No tienes grabaciones aún</p>
                <p className="text-sm text-gray-600">Presiona el botón rojo para empezar</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {recordings.map((rec) => (
                <Card key={rec.id} className="bg-[#18181b] border-white/10">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Mic className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-white truncate">{rec.title}</h3>
                      <div className="flex items-center gap-3 text-sm text-gray-500">
                        <span>{formatTime(Math.round(rec.duration_seconds))}</span>
                        {rec.bpm_used && <span>{rec.bpm_used} BPM</span>}
                        <span>{new Date(rec.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    {rec.audio_url && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => toggleSavedRecordingPlayback(rec)}
                        disabled={savedLoadingId === rec.id}
                        className="text-gray-400 hover:text-white"
                        title={savedPlayingId === rec.id ? "Pausar grabacion" : "Reproducir grabacion"}
                      >
                        {savedLoadingId === rec.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : savedPlayingId === rec.id ? (
                          <Pause className="w-4 h-4" />
                        ) : (
                          <Play className="w-4 h-4" />
                        )}
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => deleteRecording(rec.id)}
                      className="text-gray-400 hover:text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
        {savedPlaybackUrl && (
          <audio
            ref={savedAudioRef}
            src={savedPlaybackUrl}
            className="hidden"
            onEnded={() => setSavedPlayingId(null)}
            onPause={() => setSavedPlayingId(null)}
          />
        )}
      </main>

      {/* Save Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent className="bg-[#18181b] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Guardar Grabación</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm text-gray-300">Título</label>
              <Input
                value={saveTitle}
                onChange={(e) => setSaveTitle(e.target.value)}
                placeholder="Ej: Práctica de groove, Ensayo línea de bajo..."
                className="bg-black/30 border-white/10 text-white"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-gray-300">Notas (opcional)</label>
              <Input
                value={saveNotes}
                onChange={(e) => setSaveNotes(e.target.value)}
                placeholder="Notas sobre esta sesión..."
                className="bg-black/30 border-white/10 text-white"
              />
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-400">
              <span>Duración: {formatTime(recordingTime)}</span>
              {metronomeEnabled && <span>BPM: {metronomeBpm}</span>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveDialog(false)} className="border-white/10 text-gray-300">
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-primary">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RecordPage;
